import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Scale, Sparkles, ArrowRight, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const LIKELIHOOD_TONE = {
  low: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  medium: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  high: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
};

export default function DisputeWizardPage() {
  const { propertyId } = useProperty();
  const [step, setStep] = useState(1);
  const [type, setType] = useState("resident-hoa");
  const [parties, setParties] = useState("");
  const [summary, setSummary] = useState("");
  const [cc, setCc] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    if (!parties || summary.length < 10) return toast.error("Fill parties + a summary (min 10 chars)");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/ai/disputes/analyze", {
        property_id: propertyId || "all",
        dispute_type: type,
        parties, summary,
        cc_and_r_excerpt: cc || null,
      });
      setResult(data);
      setStep(3);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader eyebrow="AI Features" title="Dispute Resolution Wizard" subtitle="Claude-guided fair paths anchored in your CC&Rs. Not legal advice — a starting point for mediation." />

      <Card className="border-white/10 bg-card p-5 mb-4" data-testid="dispute-wizard">
        <div className="flex items-center gap-4 mb-4">
          <div className="h-9 w-9 rounded-md bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center">
            <Scale className="h-4 w-4 text-indigo-300" />
          </div>
          <div className="flex items-center gap-2 flex-1">
            {[1, 2, 3].map((n) => (
              <div key={n} className={"h-1 flex-1 rounded-full " + (step >= n ? "bg-indigo-500" : "bg-white/10")} data-testid={`step-${n}-bar`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="step-1">
            <div>
              <Label>Dispute type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="dispute-type" className="mt-1 bg-white/[0.02] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="resident-hoa">Resident ↔ HOA</SelectItem>
                  <SelectItem value="resident-resident">Resident ↔ Resident</SelectItem>
                  <SelectItem value="vendor-hoa">Vendor ↔ HOA</SelectItem>
                  <SelectItem value="resident-vendor">Resident ↔ Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parties involved</Label>
              <Input value={parties} onChange={(e) => setParties(e.target.value)} placeholder="e.g. Alex Morgan (Unit 12B) vs. HOA Landscape Committee" data-testid="dispute-parties" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!parties} className="bg-indigo-500 hover:bg-indigo-600" data-testid="step-1-next">
                Next <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-2">
            <div>
              <Label>What happened?</Label>
              <Textarea rows={6} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Facts, dates, prior conversations. The more specific, the better the resolution paths." data-testid="dispute-summary" className="mt-1 bg-white/[0.02] border-white/10" />
              <div className="text-[10px] text-zinc-500 mt-1">{summary.length}/4000 characters</div>
            </div>
            <div>
              <Label>Relevant CC&R excerpt (optional)</Label>
              <Textarea rows={4} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Paste the exact CC&R section(s) governing this dispute so Claude can cite them." data-testid="dispute-ccr" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="border-white/10" data-testid="step-2-back">Back</Button>
              <Button onClick={analyze} disabled={busy || summary.length < 10} className="bg-orange-500 hover:bg-orange-600" data-testid="dispute-analyze">
                {busy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Analyzing…</> : <><Sparkles className="h-3 w-3 mr-1" /> Analyze with Claude</>}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4" data-testid="step-3">
            <div className="rounded-md border border-indigo-500/30 bg-indigo-500/5 p-3 text-sm text-zinc-200" data-testid="dispute-summary-out">
              <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-1">Summary</div>
              {result.summary}
            </div>

            {result.likely_causes?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Likely causes</div>
                <ul className="space-y-1 text-sm text-zinc-300 list-disc list-inside" data-testid="dispute-causes">
                  {result.likely_causes.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Resolution paths ({result.resolution_paths?.length || 0})</div>
              <div className="space-y-2">
                {(result.resolution_paths || []).map((p, i) => (
                  <Card key={i} className="border-white/10 bg-white/[0.02] p-4" data-testid={`dispute-path-${i}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <div className="text-sm font-semibold">{p.title}</div>
                      <Badge variant="outline" className={LIKELIHOOD_TONE[p.likelihood] || "border-white/10"}>{p.likelihood} likelihood</Badge>
                      <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px]">~{p.estimated_days} days</Badge>
                      {p.who_approves && <Badge variant="outline" className="border-white/10 text-[10px]">approver: {p.who_approves}</Badge>}
                    </div>
                    <ol className="text-xs text-zinc-400 space-y-1 list-decimal list-inside">
                      {(p.steps || []).map((s, si) => <li key={si}>{s}</li>)}
                    </ol>
                  </Card>
                ))}
              </div>
            </div>

            {result.mediation_letter_draft && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Draft mediation letter</div>
                <Textarea readOnly rows={10} value={result.mediation_letter_draft} className="bg-white/[0.02] border-white/10 font-mono text-xs" data-testid="dispute-letter" />
              </div>
            )}

            {result.cc_and_r_citations?.length > 0 && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> CC&R citations
                </div>
                <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                  {result.cc_and_r_citations.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            {result.disclaimer && (
              <div className="text-[10px] text-zinc-500 border-t border-white/5 pt-3 flex items-start gap-1" data-testid="dispute-disclaimer">
                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                <div>{result.disclaimer}</div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => { setStep(1); setResult(null); }} className="border-white/10" data-testid="dispute-restart">Start over</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
